import { useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import { yUndoPluginKey } from '@tiptap/y-tiptap';
import type { Doc as YDoc } from 'yjs';

import EditorStore from 'Tiptap/stores/EditorStore';
import { HEADER_FOOTER_ZONE_HTML } from 'containers/Tiptap/lib/header-footer-extensions';
import type { HeaderFooterZone } from 'containers/Tiptap/lib/header-footer-zone';
import {
  HEADER_FOOTER_UNDO_ORIGIN,
  getDocumentSettingsMap,
  DEFAULT_FOOTER_SETTINGS,
  DEFAULT_HEADER_SETTINGS,
  DocumentFooterSettings,
  DocumentHeaderSettings,
  DocumentSettings,
  observeDocumentSettings,
  readDocumentSettings,
  toPagesCustomFormat,
  writeDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

interface UseDocumentSettingsSyncArgs {
  editor: Editor | null;
  ydoc: YDoc | null | undefined;
  isSynced: boolean;
  editorStore: EditorStore;
  reflowTick: number;
}

export interface PagesEditedStorage {
  activeEditor?: Editor | null;
  activeEditorType?: 'header' | 'footer' | 'footnotes' | 'endnotes' | null;
  activePageNumber?: number | null;
  differentFirstPage?: boolean;
  differentFirstPageFooter?: boolean;
  headerHTML?: string;
  headerFirstPageHTML?: string;
  footerHTML?: string;
  footerFirstPageHTML?: string;
}

const readPagesStorage = (editor: Editor): PagesEditedStorage | undefined =>
  (editor.storage as { pages?: PagesEditedStorage }).pages;

// Pre-zone documents stored empty content as a `<p></p>` shell, not ''.
const isBlankHtml = (html: string | undefined): boolean => {
  if (!html) return true;
  if (/<img\b/i.test(html)) return false;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return text.length === 0;
};

const withZoneFallback = (html: string | undefined): string =>
  isBlankHtml(html) ? HEADER_FOOTER_ZONE_HTML : (html as string);

const isOverlayOpen = (editor: Editor): boolean => Boolean(readPagesStorage(editor)?.activeEditor);

const activeOverlayHtml = (editor: Editor, settings: DocumentSettings): string | null => {
  const storage = readPagesStorage(editor);
  const isFirstPage = storage?.activePageNumber === 1;

  if (storage?.activeEditorType === 'header') {
    const header = settings.header ?? DEFAULT_HEADER_SETTINGS;
    return withZoneFallback(isFirstPage && header.differentFirstPage ? header.variants?.first : header.content);
  }

  if (storage?.activeEditorType === 'footer') {
    const footer = settings.footer ?? DEFAULT_FOOTER_SETTINGS;
    return withZoneFallback(isFirstPage && footer.differentFirstPage ? footer.variants?.first : footer.content);
  }

  return null;
};

const seedOverlayContent = (editor: Editor, ydoc: YDoc): void => {
  const overlay = readPagesStorage(editor)?.activeEditor;
  if (!overlay || !overlay.isEmpty) return;

  const html = activeOverlayHtml(editor, readDocumentSettings(ydoc));
  if (!html) return;

  overlay.commands.setContent(html);
  setTimeout(() => overlay.commands.focus('start'), 0);
};

/** The header or footer that was open, captured while it is open (Pages clears it on close). */
export interface OpenZone {
  type: HeaderFooterZone;
  isFirstPage: boolean;
}

const zoneFromStorage = (storage: PagesEditedStorage | undefined): OpenZone | null => {
  const type = storage?.activeEditorType;
  if (type !== 'header' && type !== 'footer') return null;
  const differentFirstPage = (type === 'header' ? storage?.differentFirstPage : storage?.differentFirstPageFooter) ?? false;
  return { type, isFirstPage: differentFirstPage && storage?.activePageNumber === 1 };
};

const readOpenZone = (editor: Editor): OpenZone | null => zoneFromStorage(readPagesStorage(editor));

// Blank content saves as '' so the document shows the empty zone layout (withZoneFallback).
const blankToEmpty = (html: string | undefined): string => (isBlankHtml(html) ? '' : (html as string));

const applyEdit = <T extends DocumentHeaderSettings | DocumentFooterSettings>(
  zoneSettings: T,
  isFirstPage: boolean,
  editedHtml: string | undefined,
): T | null => {
  const edited = blankToEmpty(editedHtml);
  const current = blankToEmpty(isFirstPage ? zoneSettings.variants?.first : zoneSettings.content);
  if (edited === current) return null;
  return isFirstPage
    ? { ...zoneSettings, variants: { ...zoneSettings.variants, first: edited } }
    : { ...zoneSettings, content: edited };
};

/** Takes the closed zone as it is, blank included, so deleting everything clears it. */
export const mergeOverlayEdits = (
  settings: DocumentSettings,
  storage: PagesEditedStorage | undefined,
  zone: OpenZone,
): Partial<Pick<DocumentSettings, HeaderFooterZone>> | null => {
  if (!storage) return null;

  if (zone.type === 'header') {
    const header = applyEdit(
      settings.header ?? DEFAULT_HEADER_SETTINGS,
      zone.isFirstPage,
      zone.isFirstPage ? storage.headerFirstPageHTML : storage.headerHTML,
    );
    return header && { header };
  }

  const footer = applyEdit(
    settings.footer ?? DEFAULT_FOOTER_SETTINGS,
    zone.isFirstPage,
    zone.isFirstPage ? storage.footerFirstPageHTML : storage.footerHTML,
  );
  return footer && { footer };
};

/**
 * Pages refreshes an open overlay with the other variant when the flag flips, and the close-time
 * persist only saves the zone open at close, so an edit typed before the toggle is folded in here.
 */
export const applyDifferentFirstPage = (
  settings: DocumentSettings,
  storage: PagesEditedStorage | undefined,
  value: boolean,
): Partial<Pick<DocumentSettings, HeaderFooterZone>> => {
  let header = settings.header ?? DEFAULT_HEADER_SETTINGS;
  let footer = settings.footer ?? DEFAULT_FOOTER_SETTINGS;

  const zone = zoneFromStorage(storage);
  const editedHtml = storage?.activeEditor?.getHTML();
  if (zone && editedHtml !== undefined) {
    if (zone.type === 'header') header = applyEdit(header, zone.isFirstPage, editedHtml) ?? header;
    else footer = applyEdit(footer, zone.isFirstPage, editedHtml) ?? footer;
  }

  return { header: { ...header, differentFirstPage: value }, footer: { ...footer, differentFirstPage: value } };
};

const persistOverlayEdits = (editor: Editor, ydoc: YDoc, zone: OpenZone): boolean => {
  const merged = mergeOverlayEdits(readDocumentSettings(ydoc), readPagesStorage(editor), zone);
  if (!merged) return false;
  const undoManager = yUndoPluginKey.getState(editor.state)?.undoManager;
  undoManager?.stopCapturing();
  writeDocumentSettings(ydoc, merged, HEADER_FOOTER_UNDO_ORIGIN);
  undoManager?.stopCapturing();
  return true;
};

const applyPageFormat = (editor: Editor, { pageSize, orientation, margins }: DocumentSettings): boolean => {
  const cmds = editor.commands as Record<string, unknown>;
  if (typeof cmds.setPageFormat !== 'function') return false;
  editor.commands.setPageFormat(toPagesCustomFormat({ pageSize, orientation, margins }));
  return true;
};

export const applySettings = (editor: Editor, settings: DocumentSettings): void => {
  if (!applyPageFormat(editor, settings)) return;

  // Re-applying header/footer while an overlay is open would reset it and discard
  // the in-progress edit. Page format above is safe; content is not.
  if (isOverlayOpen(editor)) return;

  const cmds = editor.commands as Record<string, unknown>;
  const { header, footer } = settings;
  if (typeof cmds.setHeader === 'function') editor.commands.setHeader(withZoneFallback(header?.content));
  if (header && typeof cmds.setHeaderTopMargin === 'function')
    editor.commands.setHeaderTopMargin(header.topMarginPx);
  if (typeof cmds.setDifferentFirstPage === 'function')
    editor.commands.setDifferentFirstPage(header?.differentFirstPage ?? false); // also sets footer
  if (typeof cmds.setHeaderFirstPage === 'function')
    editor.commands.setHeaderFirstPage(withZoneFallback(header?.variants?.first));
  if (typeof cmds.setDifferentOddEven === 'function') editor.commands.setDifferentOddEven(false);

  if (typeof cmds.setFooter === 'function') editor.commands.setFooter(withZoneFallback(footer?.content));
  if (footer && typeof cmds.setFooterBottomMargin === 'function')
    editor.commands.setFooterBottomMargin(footer.bottomMarginPx);
  if (typeof cmds.setFooterFirstPage === 'function')
    editor.commands.setFooterFirstPage(withZoneFallback(footer?.variants?.first));
  if (typeof cmds.setDifferentOddEvenFooter === 'function') editor.commands.setDifferentOddEvenFooter(false);

  const wrapper = (editor.view?.dom as HTMLElement | undefined)?.closest('.notion-like-editor-wrapper') as
    | HTMLElement
    | null;
  if (wrapper) {
    wrapper.dataset.headerHidden = 'false';
    wrapper.dataset.footerHidden = 'false';
  }
};

export const applyDocumentSettings = (editor: Editor, ydoc: YDoc): void =>
  applySettings(editor, readDocumentSettings(ydoc));

/**
 * Keeps the editor in sync with document settings stored in the Y.Doc:
 *  - mirrors viewMode from yjs into editorStore
 *  - re-applies page format + header/footer commands when settings change
 *  - re-runs setPageFormat when the Document tab becomes visible so the
 *    Pages extension recomputes pagination against a measurable container
 *    (pagination computed while display:none stays stuck at zero width).
 */
export function useDocumentSettingsSync({
  editor,
  ydoc,
  isSynced,
  editorStore,
  reflowTick,
}: UseDocumentSettingsSyncArgs): void {
  useEffect(() => {
    if (!ydoc || !isSynced) return undefined;
    const sync = (): void => {
      const settings = readDocumentSettings(ydoc);
      if (settings.viewMode !== editorStore.viewMode) {
        editorStore.setViewMode(settings.viewMode);
      }
    };
    sync();
    return observeDocumentSettings(ydoc, sync);
  }, [editorStore, isSynced, ydoc]);

  useEffect(() => {
    if (!editor || !ydoc || !isSynced) return undefined;
    const apply = (): void => applyDocumentSettings(editor, ydoc);
    apply();
    let pending: ReturnType<typeof setTimeout> | null = null;
    const unobserve = observeDocumentSettings(ydoc, ({ isLocal }) => {
      if (isLocal) {
        apply();
        return;
      }
      // A remote update can change the body in the same Yjs transaction. Dispatching from inside
      // it makes y-prosemirror write the not-yet-synced body back over the incoming one.
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = null;
        if (!editor.isDestroyed) apply();
      }, 0);
    });
    return () => {
      unobserve();
      if (pending) clearTimeout(pending);
    };
  }, [editor, isSynced, ydoc]);

  // Persist inline header/footer edits back to the Y.Doc when the editing
  // overlay closes, so they survive reopen/reload, sync to collaborators, and
  // feed exports. Closing the overlay re-enables the body editor, which emits
  // an update/transaction — we watch the overlay open→closed transition there.
  useEffect(() => {
    if (!editor || !ydoc || !isSynced) return undefined;
    let overlayWasOpen = isOverlayOpen(editor);
    let openZone = readOpenZone(editor);
    const handle = (): void => {
      const open = isOverlayOpen(editor);
      if (!overlayWasOpen && open) seedOverlayContent(editor, ydoc);
      if (open) openZone = readOpenZone(editor) ?? openZone;
      if (overlayWasOpen && !open) {
        if (openZone && persistOverlayEdits(editor, ydoc, openZone)) editorStore.setHasUnpublishedChanges(true);
        openZone = null;
        // The settings observer fires mid-close while isOverlayOpen still guards,
        // so it skips re-applying. Re-apply next tick (overlay now closed) so the
        // edited content shows without a reload.
        setTimeout(() => applyDocumentSettings(editor, ydoc), 0);
      }
      overlayWasOpen = open;
    };
    editor.on('update', handle);
    editor.on('transaction', handle);
    return () => {
      editor.off('update', handle);
      editor.off('transaction', handle);
    };
  }, [editor, editorStore, isSynced, ydoc]);

  // The overlay editor keeps its own history, so undo only reaches a saved header/footer
  // edit if the main editor's undo manager tracks the settings map for that origin.
  useEffect(() => {
    if (!editor || !ydoc || !isSynced) return;
    const undoManager = yUndoPluginKey.getState(editor.state)?.undoManager;
    if (!undoManager) return;
    undoManager.addToScope(getDocumentSettingsMap(ydoc));
    undoManager.addTrackedOrigin(HEADER_FOOTER_UNDO_ORIGIN);
  }, [editor, isSynced, ydoc]);

  useEffect(() => {
    if (!editor || !ydoc || !isSynced || reflowTick === 0) return;
    applyPageFormat(editor, readDocumentSettings(ydoc));
  }, [editor, ydoc, isSynced, reflowTick]);
}
